import { StatusInterface, UserInfoInterface, UserUpdatePasswordRequestInterface } from '@elearning-platform/api-types/interfaces';
import { AxiosError, AxiosResponse } from 'axios';

import {
  FetchRegistrationFormParams,
  RefreshAccessTokenResponse,
  RegistrationForm,
  RemindPasswordInfo,
  SendActivationFormParams,
  SendRegistrationFormParams,
  SendRegistrationFormResponse,
  SendRegistrationFormWithTokenParams,
} from '../../interfaces';
import { ApiReject, ApiResolve } from '../../types/api';
import { api } from '../api';
import { phpApi } from '../default';

export const fetchRegistrationForm: (
  params: FetchRegistrationFormParams
) => Promise<RegistrationForm> = (
  params: FetchRegistrationFormParams
): Promise<RegistrationForm> => new Promise(
  (resolve: ApiResolve<RegistrationForm>, reject: (reason: AxiosError<{ message?: string }>) => void): Promise<void> => (
    phpApi.get(`/api/user/register${params.hash ? `/${params.hash}` : ''}`)
      .then((response: AxiosResponse<RegistrationForm>): void => resolve(response.data))
      .catch((err: AxiosError<{ message?: string }>): void => {
        const error: AxiosError<{ message?: string }> = err as AxiosError<{ message?: string }>;

        reject({ message: error.response?.data?.message } as AxiosError<{ message?: string }>);
      })
  )
);

export const sendRegistrationForm: (
  params: SendRegistrationFormParams
) => Promise<SendRegistrationFormResponse> = (
  params: SendRegistrationFormParams
): Promise<SendRegistrationFormResponse> => new Promise(
  (
    resolve: ApiResolve<SendRegistrationFormResponse>,
    reject: (reason: AxiosError<RegistrationForm>) => void
  ): Promise<void | never[]> => (
    phpApi.post('/api/user/register', params.data)
      .then((response: AxiosResponse<SendRegistrationFormResponse>): void => resolve(response.data))
      .catch((err: AxiosError<RegistrationForm>): void => {
        reject(err);
      })
  )
);

export const sendRegistrationFormWithToken: (
  params: SendRegistrationFormWithTokenParams
) => Promise<SendRegistrationFormResponse> = (
  params: SendRegistrationFormWithTokenParams
): Promise<SendRegistrationFormResponse> => new Promise(
  (
    resolve: ApiResolve<SendRegistrationFormResponse>,
    reject: (reason: AxiosError<RegistrationForm>) => void
  ): Promise<void | never[]> => (
    phpApi.post(`/api/user/register/${params.hash}`, params.data)
      .then((response: AxiosResponse<SendRegistrationFormResponse>): void => resolve(response.data))
      .catch((err: AxiosError<RegistrationForm>): void => {
        reject(err);
      })
  )
);

export const remindPassword: (login: string) => Promise<AxiosResponse<RemindPasswordInfo>> = (
  login: string
): Promise<AxiosResponse<RemindPasswordInfo>> => phpApi.post('/api/user/remind-password', { login });

export const fetchActivationForm: (
  token: string | null
) => Promise<RegistrationForm> = (
  token: string | null
): Promise<RegistrationForm> => new Promise(
  (resolve: ApiResolve<RegistrationForm>, reject: (reason: AxiosError<{ message?: string }>) => void): Promise<void> => (
    phpApi.get(`/api/user/activation/${token}`)
      .then((response: AxiosResponse<RegistrationForm>): void => resolve(response.data))
      .catch((err: AxiosError<{ message?: string }>): void => {
        const error: AxiosError<{ message?: string }> = err as AxiosError<{ message?: string }>;

        reject({ message: error.response?.data?.message } as AxiosError<{ message?: string }>);
      })
  )
);

export const sendActivationForm: (
  params: SendActivationFormParams
) => Promise<SendRegistrationFormResponse> = (
  params: SendActivationFormParams
): Promise<SendRegistrationFormResponse> => new Promise(
  (
    resolve: ApiResolve<SendRegistrationFormResponse>,
    reject: (reason: AxiosError<RegistrationForm>) => void
  ): Promise<void | never[]> => (
    phpApi.post(`/api/user/activation/${params.token}`, params.data)
      .then((response: AxiosResponse<SendRegistrationFormResponse>): void => resolve(response.data))
      .catch((err: AxiosError<RegistrationForm>): void => {
        reject(err);
      })
  )
);

export const updatePassword: (payload: UserUpdatePasswordRequestInterface) => Promise<StatusInterface>
= (payload: UserUpdatePasswordRequestInterface): Promise<StatusInterface> => api.post('v1/auth/update-password', payload);

// TODO: remove when proxy for this endpoint will work on be node
export const getUserInfo: () => Promise<UserInfoInterface> = (): Promise<UserInfoInterface> => new Promise(
  (resolve: ApiResolve<UserInfoInterface>, reject: ApiReject): Promise<void> => (
    phpApi.get('/api/user/info')
      .then((response: AxiosResponse<UserInfoInterface>): void => resolve(response.data))
      .catch(reject)
  )
);

export const refreshAccessToken: () => Promise<RefreshAccessTokenResponse>
  = (): Promise<RefreshAccessTokenResponse> => phpApi.post('/api/user/refresh', undefined, { withCredentials: true });

export const extendRefreshToken: () => Promise<void>
  = (): Promise<void> => phpApi.post('/api/user/extend-session', undefined, { withCredentials: true });
